import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Loader2, ChevronLeft, ChevronRight, ShieldCheck } from "lucide-react";

// Use CDN worker that exactly matches installed pdfjs-dist version (classic .min.js for CRA/webpack compatibility)
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/**
 * SecurePdfViewer
 *  - Canvas-only rendering (no browser toolbar / download / print).
 *  - Disables right-click, Ctrl/Cmd+S, Ctrl+P, Ctrl+U, drag & drop, text selection.
 *  - Overlay to block scraping the canvas via inspect > save image.
 */
export const SecurePdfViewer = ({ file, testIdPrefix = "cert", accent = "#12355B" }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [width, setWidth] = useState(560);
  const wrapperRef = useRef(null);
  const pdfOptions = useMemo(() => ({ isEvalSupported: false }), []);

  useEffect(() => {
    const measure = () => {
      if (!wrapperRef.current) return;
      const w = wrapperRef.current.getBoundingClientRect().width;
      setWidth(Math.max(240, Math.floor(w)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const block = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };
    const blockKey = (e) => {
      const k = (e.key || "").toLowerCase();
      // Block Save, Print, View-source, dev tools save shortcuts
      if ((e.ctrlKey || e.metaKey) && ["s", "p", "u"].includes(k)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    el.addEventListener("contextmenu", block);
    el.addEventListener("dragstart", block);
    el.addEventListener("selectstart", block);
    el.addEventListener("copy", block);
    window.addEventListener("keydown", blockKey);

    return () => {
      el.removeEventListener("contextmenu", block);
      el.removeEventListener("dragstart", block);
      el.removeEventListener("selectstart", block);
      el.removeEventListener("copy", block);
      window.removeEventListener("keydown", blockKey);
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      data-testid={`${testIdPrefix}-secure-viewer`}
      className="relative w-full select-none"
      style={{ userSelect: "none", WebkitUserSelect: "none", MozUserSelect: "none" }}
    >
      <div className="relative rounded-xl border border-neutral-200 overflow-hidden bg-neutral-50">
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          loading={
            <div className="aspect-[4/3] w-full flex items-center justify-center text-neutral-400 gap-2">
              <Loader2 className="animate-spin" size={16} /> Loading certificate…
            </div>
          }
          error={
            <div className="aspect-[4/3] w-full flex items-center justify-center text-neutral-500 text-sm">
              Preview unavailable.
            </div>
          }
          options={pdfOptions}
        >
          <Page
            pageNumber={pageNumber}
            width={width}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            className="mx-auto"
          />
        </Document>

        {/* Transparent overlay to prevent right-click on canvas / touch save */}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-10"
          style={{ background: "transparent", pointerEvents: "auto" }}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        />

        {/* Watermark */}
        <div className="absolute inset-0 z-20 pointer-events-none flex items-center justify-center">
          <div
            className="font-heading text-4xl md:text-5xl tracking-tight opacity-[0.07] rotate-[-24deg] whitespace-nowrap"
            style={{ color: accent }}
          >
            R K STEEL · VIEW ONLY
          </div>
        </div>
      </div>

      {numPages > 1 && (
        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            data-testid={`${testIdPrefix}-page-prev`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-neutral-200 text-xs font-heading font-medium text-[#222629] hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <div className="text-xs font-mono text-neutral-500">
            Page {pageNumber} / {numPages}
          </div>
          <button
            type="button"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            data-testid={`${testIdPrefix}-page-next`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-neutral-200 text-xs font-heading font-medium text-[#222629] hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase text-neutral-500">
        <ShieldCheck size={13} className="text-[#2F9E44]" />
        View-only preview · downloading, printing and saving are disabled
      </div>
    </div>
  );
};

export default SecurePdfViewer;
